import React, { useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import { List, NumberedList, ListItem } from '@fiverr-private/list';
import { Link } from '@fiverr-private/typography';
import { Box } from '@fiverr-private/theme';
import { useEditorAsyncProps } from '../listings/hooks/useEditorAsyncProps';

import './styles.scss';

interface Link {
    id: string;
    text: string;
    link: string;
    childrenIds?: string[];
}

interface TableOfContentsProps {
    h2Links?: Link[];
    h3Links?: Link[];
    isNumbered: boolean;
}

const TableOfContentsContainer = ({ children }: { children: React.ReactNode }) => (
    <Box className="section-table-of-contents" backgroundColor="blue_100">
        {children}
    </Box>
);

const TableOfContents = ({ isNumbered, ...props }: TableOfContentsProps) => {
    const [h2Links, setH2Links] = useState(props.h2Links || []);
    const [h3Links, setH3Links] = useState(props.h3Links || []);

    const editorAsyncProps: Record<string, any> = useEditorAsyncProps('TableOfContents');

    useEffect(() => {
        if (!isEmpty(editorAsyncProps?.h2Links)) {
            setH2Links(editorAsyncProps.h2Links);
        }
        if (!isEmpty(editorAsyncProps?.h3Links)) {
            setH3Links(editorAsyncProps.h3Links);
        }
    }, [editorAsyncProps]);

    const ListTag = isNumbered ? NumberedList : List;

    const mapIdsToLinks = (ids: string[], links: Link[]): Link[] =>
        ids
            .map((id) => {
                const link = links.find((link) => link.id === id);
                return link;
            })
            .filter(Boolean) as Link[];

    const renderLinks = (links: Link[], childrenLinks?: Link[], withPadding?: boolean) => (
        <ListTag paddingLeft={isNumbered || withPadding ? '5' : '0'}>
            {links?.map((heading, idx) => (
                <React.Fragment key={idx}>
                    <ListItem>
                        <Link color={{ hover: 'blue_700' }} lineHeight={{ default: 'b_md' }} href={heading.link}>
                            {heading.text}
                        </Link>
                        {!!heading.childrenIds?.length &&
                            !!childrenLinks?.length &&
                            renderLinks(mapIdsToLinks(heading.childrenIds, childrenLinks), [], true)}
                    </ListItem>
                </React.Fragment>
            ))}
        </ListTag>
    );

    if (h2Links.length) {
        return <TableOfContentsContainer>{renderLinks(h2Links, h3Links)}</TableOfContentsContainer>;
    }
    return <TableOfContentsContainer>{renderLinks(h3Links)}</TableOfContentsContainer>;
};

export default TableOfContents;
